import React from 'react';
import { Outlet, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { ArrowLeft, Loader2, AlertCircle, UserRound } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { AmbianceBand } from '../Ambiance/AmbianceBand';
import { pillSecondary } from '../ui/pills';
import { PIPELINE_STAGE_LABELS, PipelineStage } from './pipelineStages';
import { FicheDocument } from './PipelineDocuments';

/**
 * LE DOSSIER, PAS LA PRIVATISATION. Ce qui est identifié ici est `dossier_id` : la privatisation
 * (`event_id`) n'existe qu'à partir de la signature du devis, et vaut `null` avant. Tout écran qui affiche
 * une date, un nombre de couverts ou un lien vers Événementiel doit donc supporter son absence.
 */
export interface Dossier {
    dossier_id: string;
    label: string;
    type: string;
    first_name: string;
    last_name: string;
    company_name: string;
    organizer_name: string;
    organizer_email: string;
    organizer_phone: string;
    email: string;
    phone: string;
    stage: PipelineStage;
    stage_since?: string | null;
    formule_retenue: string;
    devis_amount_ttc?: number | null;
    plaquette_sent_at?: string | null;
    client_reply_at?: string | null;
    brief_collected_at?: string | null;
    devis_sent_at?: string | null;
    devis_signed_at?: string | null;
    /** Le lien public du devis : à qui il est parti. */
    devis_sent_to?: string;
    /** Le client a DÉPOSÉ son exemplaire signé. Distinct de devis_signed_at, que seule la validation
     *  du commercial pose : recevoir n'est pas valider. */
    devis_signed_received_at?: string | null;
    devis_signed_document_id?: string | null;
    /** Valeurs reprises des réponses du Brief pour pré-remplir la création de la privatisation — voir
     *  CreerPrivatisationPanel.tsx. Absent si aucune réponse liée à un champ n'a été donnée. */
    brief_prefill?: { date?: string; start_time?: string; end_time?: string; num_people?: number } | null;
    event_created_at?: string | null;
    prestataires_at?: string | null;
    fournisseurs_at?: string | null;
    menus_started_at?: string | null;
    jour_j_at?: string | null;
    closed_at?: string | null;
    lost_at?: string | null;
    lost_reason: string;
    documents: FicheDocument[];
    /** La privatisation dans Événementiel — null tant que le devis n'est pas signé. */
    event?: { id: string; start_time: string; end_time: string; num_people?: number | null } | null;
    collecte_status?: string | null;
    rendez_vous?: { id: string; subject: string; appointment_date: string; start_time: string; status: string } | null;
    /** Ce qu'est devenue la plaquette envoyée : le client a-t-il suivi le lien et répondu ? */
    plaquette_response?: {
        request_id: string;
        template_name: string;
        offered_appointment: boolean;
        responded: boolean;
        responded_at: string | null;
        appointment: { id: string; appointment_date: string; start_time: string } | null;
    } | null;
    journal: { from_stage: string | null; to_stage: string; actor_label: string; note: string; changed_at: string }[];
}

interface DossierContext {
    dossier: Dossier;
    rafraichir: () => void;
}

/**
 * Contexte du dossier, à consommer depuis n'importe quel onglet. Transposé du `useProjectContext()` de
 * RyoFlow : le layout charge la fiche UNE fois et la partage, au lieu que chaque onglet la recharge en
 * arrivant. Changer d'onglet devient instantané, et tous les onglets voient le même état.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook colocalisé avec son layout, comme chez RyoFlow
export function useDossierContext() {
    return useOutletContext<DossierContext>();
}

const fmtDateLongue = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }); } catch { return ''; }
};
const fmtHeure = (d?: string | null) => {
    if (!d) return '';
    try { return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); } catch { return ''; }
};
const majuscule = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

/** Ligne sous le titre de la bande (maquette 18a) : « Samedi 4 octobre 2026 · 18:00 – 02:00 · 120 convives ».
 *  Avant la signature il n'y a ni date ni couverts : on montre alors de quoi rappeler le client, qui est
 *  la seule action utile à ce stade — même règle qu'avant la refonte. */
const sousLigne = (dossier: Dossier): string => {
    if (dossier.event) {
        const morceaux = [majuscule(fmtDateLongue(dossier.event.start_time))];
        const debut = fmtHeure(dossier.event.start_time);
        const fin = fmtHeure(dossier.event.end_time);
        if (debut) morceaux.push(fin ? `${debut} – ${fin}` : debut);
        if (dossier.event.num_people) morceaux.push(`${dossier.event.num_people} convive${dossier.event.num_people > 1 ? 's' : ''}`);
        return morceaux.filter(Boolean).join(' · ');
    }
    return [dossier.phone, dossier.email].filter(Boolean).join(' · ') || 'Aucune coordonnée renseignée';
};

const PILULE_BANDE = 'inline-flex items-center h-[22px] px-[9px] rounded-full bg-surface/75 border border-border text-content-muted text-[10px] font-bold whitespace-nowrap shrink-0';

/**
 * Un dossier = une seule page (fusion des anciens onglets Pipeline / Brief / Documents / Menus & Jour J /
 * Historique en un flux continu, demande d'Emeni, 2026-09-02 : « les onglets ne servent plus à rien, tout
 * doit se passer dans Pipeline »).
 *
 * Refonte design lot 9 (maquette 18a, patron « fiche-dossier ») : la fiche est UNE carte de 14 px —
 * bande d'ambiance de 104 px qui nomme le dossier et son type, puis les sections rendues par la route
 * `pipeline` (avancement, étape en cours, faits, suivi chiffré, documents, journal, pied). L'animation ne
 * vit que dans la bande, rien ne bouge sous elle (passation §7).
 *
 * La fiche est chargée une seule fois ici et partagée par `Outlet context`, avec une fonction de
 * rafraîchissement commune.
 */
export function SuiviPrivatisationLayout() {
    const { dossierId } = useParams<{ dossierId: string }>();
    const navigate = useNavigate();
    const [dossier, setDossier] = React.useState<Dossier | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');

    const charger = React.useCallback(() => {
        if (!dossierId) return;
        moduleApi.getDossierPrivatisation(dossierId)
            .then((r: any) => { setDossier(r); setErreur(''); })
            .catch((e: any) => setErreur(e?.message || 'Dossier introuvable.'))
            .finally(() => setLoading(false));
    }, [dossierId]);
    React.useEffect(() => { charger(); }, [charger]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="animate-spin text-content-muted" size={24} />
                <span className="text-xs text-content-muted">Chargement du dossier...</span>
            </div>
        );
    }

    if (erreur || !dossier || !dossierId) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <AlertCircle className="text-state-danger" size={26} />
                <span className="text-sm font-semibold text-content">{erreur || 'Dossier introuvable'}</span>
                <button type="button" onClick={() => navigate('/suivi-privatisation')} className={pillSecondary(34)}>
                    <ArrowLeft size={13} /> Retour au suivi
                </button>
            </div>
        );
    }

    const referent = [dossier.organizer_name, dossier.organizer_phone, dossier.organizer_email].filter(Boolean).join(' · ');

    return (
        <div className="space-y-3 max-w-[1040px]">
            <button type="button" onClick={() => navigate('/suivi-privatisation')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content">
                <ArrowLeft size={14} /> Retour au suivi
            </button>

            <div className="rounded-[14px] border border-border bg-surface-soft overflow-hidden">
                <AmbianceBand className="h-[104px] border-b border-border">
                    <div className="relative z-[2] h-[104px] px-5 py-[15px] flex flex-col justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className={`${PILULE_BANDE} uppercase tracking-[.06em]`}>
                                {dossier.type === 'PROFESSIONNEL' ? 'Client professionnel' : 'Client particulier'}
                            </span>
                            <span className={PILULE_BANDE}>{PIPELINE_STAGE_LABELS[dossier.stage]}</span>
                        </div>
                        <div className="min-w-0">
                            <div className="text-[21px] font-bold tracking-[-0.02em] text-content leading-tight truncate">{dossier.label}</div>
                            <div className="mt-px text-[12.5px] text-content-muted truncate">
                                {sousLigne(dossier)}
                                {/* Le référent reste affiché même une fois la date connue : c'est LUI qu'on
                                    rappelle, pas le standard de la société (demande d'Emeni, 2026-08-30). */}
                                {referent && <span title="Référent / organisateur"> · <UserRound size={11} className="inline -mt-0.5" /> {referent}</span>}
                            </div>
                        </div>
                    </div>
                </AmbianceBand>

                <Outlet context={{ dossier, rafraichir: charger } satisfies DossierContext} />
            </div>
        </div>
    );
}
